import React, { useCallback } from 'react';
import { useSelector } from '@xstate/react';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { ActorRefFrom } from 'xstate';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Address } from './types';
import paymentDetailsMachine from './service/payment-details-machine';
import BillingAddress from './billing-address';
import WrappedPaymentElement from './wrapped-payment-element';
import { useApp } from '../../hooks/use-app';
import Button from '../../components/button';

interface PaymentDetailsProps {
  interpreter: ActorRefFrom<typeof paymentDetailsMachine>;
}

const stripeSelector = (state: any) => state.context.stripe;
const clientSecretSelector = (state: any) => state.context.clientSecret;
const addressSelector = (state: any) => state.context.address;
const isInitializingStripeSelector = (state: any) => state.matches('initializingStripe');
const isGettingAddressSelector = (state: any) => state.matches('gettingAddress');
const isGettingPaymentDetailsSelector = (state: any) => state.matches('gettingPaymentDetails');
const isSubmittingPaymentDetailsSelector = (state: any) => state.matches('submittingPaymentDetails');
const isPaymentDetailsCompleteSelector = (state: any) => state.matches('paymentDetailsComplete');
const isStripeLoadErrorSelector = (state: any) => state.matches('stripeLoadError');

function PaymentDetails({ interpreter }: PaymentDetailsProps) {
  const app = useApp();
  const stripe: Stripe = useSelector(interpreter, stripeSelector);
  const clientSecret: string = useSelector(interpreter, clientSecretSelector);
  const address: Address = useSelector(interpreter, addressSelector);
  const isInitializingStripe: boolean = useSelector(interpreter, isInitializingStripeSelector);
  const isGettingPaymentDetails: boolean = useSelector(
    interpreter,
    isGettingPaymentDetailsSelector,
  );
  const isSubmittingPaymentDetails: boolean = useSelector(
    interpreter,
    isSubmittingPaymentDetailsSelector,
  );
  const isGettingAddress: boolean = useSelector(interpreter, isGettingAddressSelector);
  const isPaymentDetailsComplete: boolean = useSelector(
    interpreter,
    isPaymentDetailsCompleteSelector,
  );

  const isStripeLoadError: boolean = useSelector(interpreter, isStripeLoadErrorSelector);

  const handleChangeAddress = useCallback((newAddress: Address) => {
    interpreter.send({
      type: 'ADDRESS_UPDATED',
      address: newAddress,
      organizationId: app.globals.organization.id,
    });
  }, [interpreter]);

  const handleElementsLoaded = useCallback((elements: StripeElements) => {
    interpreter.send({ type: 'STRIPE_ELEMENTS_LOADED', elements });
  }, [interpreter]);

  const handleRetry = useCallback(() => {
    interpreter.send({ type: 'RETRY' });
  }, [interpreter]);

  if (isStripeLoadError) return <Button onClick={handleRetry}>Retry Connection</Button>;

  if (isPaymentDetailsComplete) return <div>Payment Details complete</div>;

  return (
    <>
      {isInitializingStripe && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}
      {isGettingAddress && (
        <BillingAddress value={address} onChange={handleChangeAddress} />
      )}
      {(isGettingPaymentDetails || isSubmittingPaymentDetails) && (
        <Elements stripe={stripe} options={{ clientSecret }}>
          <WrappedPaymentElement onElementsLoaded={handleElementsLoaded} />
        </Elements>
      )}
    </>
  );
}

export default PaymentDetails;
